// The meeting the room is in, and the agenda for it (kanban 108573ba, owner
// ask on review vhztut 2026-10-07: "agenda for today's alpha meeting ... make
// sure it properly understands which meeting it should be talking about").
//
// Pure rules, no React. A SERIES is a recurring meeting the roadmap already
// knows from its sources; the registry and the gate (who may open which
// series) live on the backend, services/roadmap/meeting_series.py, and reach
// the page through GET /my-roadmap/meetings (owner on review 311c384e
// 2026-10-08: "access to these is scoped based on who the user is"). Each
// series owns the initiatives tagged with its teams; a series with no teams
// is the whole roadmap. The agenda for a series is built from the same facts
// the rest of the page shows: late / at risk / stuck, waiting on a live
// blocker or an outside party, questions due in the room this week, and
// dated items due within three weeks for a one-sentence status. When Merlin
// starts writing agendas on each run, its lines slot in ahead of these
// deterministic ones; this stays the fallback.

import type { ClarifyingQuestion, InitiativeEvidence, InitiativePriority } from '../../services/roadmapReports';
import type { MeetingInitiative, MeetingSeries } from '../../services/myRoadmap';
import { decideByFromQuestion, isParked, questionAddressees, waitingLines } from './roadmapViewUtils';
import { addDays, isoDay, parseDay } from './roadmapTimelineUtils';

export type { MeetingInitiative, MeetingSeries } from '../../services/myRoadmap';

/** A series id as the backend names it ('alpha', 'delta', 'mgmt', 'checkin'). */
export type SeriesId = string;

/** The series with this id, else the first one; null when there are none. */
export function seriesById(series: readonly MeetingSeries[], id: string | null | undefined): MeetingSeries | null {
  return series.find((s) => s.id === id) ?? series[0] ?? null;
}

/** The next occurrence of a series on or after `today` (today itself counts). */
export function nextMeetingDate(series: MeetingSeries, today: Date): Date {
  const delta = (series.weekday - today.getDay() + 7) % 7;
  return addDays(today, delta);
}

/** The series whose next meeting is soonest: the one the room is most likely
 * in. Ties (two meetings the same day) go to the earlier time of day, then to
 * list order. Null when the person may open no series. */
export function defaultSeries(series: readonly MeetingSeries[], today: Date): MeetingSeries | null {
  const minutes = (t: string) => { const m = /(\d+):(\d+)\s*(AM|PM)/i.exec(t); if (!m) return 0; let h = Number(m[1]) % 12; if (/pm/i.test(m[3])) h += 12; return h * 60 + Number(m[2]); };
  return [...series]
    .map((s, n) => ({ s, n, days: (s.weekday - today.getDay() + 7) % 7 }))
    .sort((a, b) => a.days - b.days || minutes(a.s.time) - minutes(b.s.time) || a.n - b.n)[0]?.s ?? null;
}

export function meetingLabel(series: MeetingSeries, today: Date): string {
  const d = nextMeetingDate(series, today);
  const sameDay = isoDay(d) === isoDay(today);
  const dayWord = sameDay ? 'today' : d.toLocaleDateString(undefined, { weekday: 'long' });
  const date = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  return `${dayWord}, ${date}, ${series.time}`;
}

export function belongsToSeries(i: Pick<MeetingInitiative, 'teams'>, series: MeetingSeries): boolean {
  if (series.teams.length === 0) return true;
  const want = new Set(series.teams.map((t) => t.toLowerCase()));
  return (i.teams ?? []).some((t) => want.has(t.toLowerCase()));
}

const teamKey = (teams: readonly string[]) => [...new Set(teams.map((t) => t.trim().toLowerCase()))].sort().join('\u0000');

/** Two team lists name the same teams (case-folded, order and repeats aside). */
export function sameTeams(a: readonly string[], b: readonly string[]): boolean {
  return teamKey(a) === teamKey(b);
}

/** The one series whose teams are exactly the page's Team filter, so a
 * filter to the Alpha team IS the Alpha Team meeting (kanban 4d581b21: the
 * agenda read differently filtered to a team than with that team's meeting
 * picked). An empty filter resolves nothing: it says "no narrowing", not
 * "the whole-roadmap meeting", and the calendar default stands. Null when
 * no series matches, or when two do (the page then keeps its pick). */
export function seriesForTeams(series: readonly MeetingSeries[], teams: readonly string[]): MeetingSeries | null {
  if (teams.length === 0) return null;
  const hits = series.filter((s) => s.teams.length > 0 && sameTeams(s.teams, teams));
  return hits.length === 1 ? hits[0] : null;
}

export type AgendaKind = 'decide' | 'unblock' | 'hear';
export const AGENDA_KIND_LABELS: Record<AgendaKind, string> = { decide: 'Decide', unblock: 'Unblock', hear: 'Hear' };
export const AGENDA_KIND_HINTS: Record<AgendaKind, string> = {
  decide: 'A question is due in the room this week.',
  unblock: 'Late, at risk, stuck, or waiting on someone.',
  hear: 'Due within three weeks: one sentence of status.',
};

/** The rest of the room's initiatives, below what needs the room, by
 * urgency (owner on review 4yi1x3, 2026-10-21: "a critical section for what
 * is showing there now and then still show the rest ... arranged and color
 * coded by importance/urgency"). */
export type RestTier = 'soon' | 'later' | 'undated';
export const REST_TIER_LABELS: Record<RestTier, string> = { soon: 'Coming up', later: 'On track', undated: 'No date' };
export const REST_TIER_HINTS: Record<RestTier, string> = {
  soon: 'Due within six weeks, or marked top or high priority.',
  later: 'Dated further out; nothing is waiting on it.',
  undated: 'No target date stated yet.',
};
/** Every row's urgency, agenda and rest alike, for the colour down the list. */
export type Urgency = AgendaKind | RestTier;
export const URGENCY_RANK: Record<Urgency, number> = { decide: 0, unblock: 1, hear: 2, soon: 3, later: 4, undated: 5 };

export interface AgendaItem {
  key: string;
  title: string;
  kind: AgendaKind;
  /** Why it is on the agenda, in order of weight ("Late: target was Oct 2", "Waiting on …"). */
  reasons: string[];
  who: string | null;
  teams: string[];
  target: string | null;
  /** Live questions due in the room this week: these go on the agenda line. */
  dueQuestions: ClarifyingQuestion[];
  /** The item's other live questions, for the pop-up. */
  laterQuestions: ClarifyingQuestion[];
  /** Suggested minutes. */
  minutes: number;
  initiative: MeetingInitiative;
}

export interface RestItem {
  key: string;
  title: string;
  tier: RestTier;
  who: string | null;
  teams: string[];
  target: string | null;
  priority: InitiativePriority | null;
  /** Live questions on it, none due in the room this week. */
  laterQuestions: ClarifyingQuestion[];
  initiative: MeetingInitiative;
}

export interface SeriesAgenda {
  series: MeetingSeries;
  date: Date;
  /** What needs the room: decide, then unblock, then hear. */
  items: AgendaItem[];
  /** The room's other active initiatives, by urgency: coming up, on track, undated. */
  rest: RestItem[];
  /** Live questions addressed to someone in the room whose initiative is not on the agenda. */
  alsoForTheRoom: ClarifyingQuestion[];
  totalMinutes: number;
  /** What the view left off this room entirely, so the panel can say so
   * instead of letting "4 items" read as the whole roadmap (kanban c3097c0a,
   * admin 2026-10-21: "why does this only show 4 things? aren't there more?"). */
  omitted: AgendaOmitted;
  /** Active initiatives on the view, this meeting's and others' alike. */
  activeTotal: number;
}

export interface AgendaOmitted {
  /** Active initiatives tagged with another meeting's teams. Counted over
   * every initiative (`all`), not the filtered view: picking a meeting also
   * sets the Team filter (kanban 4d581b21), which would otherwise hide the
   * very items this count explains. */
  offSeries: number;
  /** Done, dropped or parked initiatives, counted over every initiative. */
  closed: number;
}

/** A question is for THIS meeting when its decide-by date falls on or before
 * the meeting day plus this many days (the group decides it in the room). */
export const DECIDE_WINDOW_DAYS = 6;
/** A dated item earns a one-sentence status when its target is this close. */
export const HEAR_WINDOW_DAYS = 21;
/** Beyond the hear window, an item this close (or marked top / high) is "coming up". */
export const SOON_WINDOW_DAYS = 42;
const PRIORITY_RANK: Record<InitiativePriority, number> = { top: 0, high: 1, normal: 2, low: 3 };
const REST_RANK: Record<RestTier, number> = { soon: 0, later: 1, undated: 2 };

const fmt = (d: Date) => d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
const KIND_RANK: Record<AgendaKind, number> = { decide: 0, unblock: 1, hear: 2 };

export function agendaForSeries(args: {
  series: MeetingSeries;
  /** The initiatives on the view (the page's filters applied, when it has any). */
  initiatives: MeetingInitiative[];
  /** Every initiative, so a blocker outside the filter is still named; defaults to `initiatives`. */
  all?: MeetingInitiative[];
  questions: ClarifyingQuestion[];
  today: Date;
}): SeriesAgenda {
  const { series, initiatives, all = initiatives, questions, today } = args;
  const date = nextMeetingDate(series, today);
  const year = today.getFullYear();
  const isActive = (i: MeetingInitiative) => i.status !== 'done' && i.status !== 'dropped' && !isParked(i);
  const active = initiatives.filter(isActive);
  const activeAll = all === initiatives ? active : all.filter(isActive);
  const waiting = new Map(waitingLines(active, all).map((w) => [w.initiative.initiative_key, w]));
  const live = questions.filter((q) => !q.response);
  const dueForRoom = (q: ClarifyingQuestion) => {
    const by = parseDay(decideByFromQuestion(q.question, year));
    return Boolean(by && by.getTime() <= addDays(date, DECIDE_WINDOW_DAYS).getTime());
  };

  const items: AgendaItem[] = [];
  const rest: RestItem[] = [];
  const offSeries = activeAll.filter((i) => !belongsToSeries(i, series)).length;
  for (const i of active) {
    if (!belongsToSeries(i, series)) continue;
    const mine = live.filter((q) => q.initiative_key === i.initiative_key);
    const due = mine.filter(dueForRoom);
    const reasons: string[] = [];
    let kind: AgendaKind | null = null;
    const target = parseDay(i.target_date);
    if (i.health === 'late') { kind = 'unblock'; reasons.push(`Late: target was ${target ? fmt(target) : i.target_date}`); }
    else if (i.health === 'at_risk') { kind = 'unblock'; reasons.push('At risk: a blocker lands after its target'); }
    else if (i.health === 'stuck') { kind = 'unblock'; reasons.push('No movement in two weeks'); }
    const w = waiting.get(i.initiative_key);
    if (w && w.on.length) {
      kind = kind ?? 'unblock';
      // waitingLines names an unnamed blocker with a placeholder title; say it in words here.
      const named = w.on.filter((o) => o.key !== null || !/nobody has said/i.test(o.title));
      reasons.push(named.length ? `Waiting on ${named.map((o) => o.title).join('; ')}` : 'Blocked, but nobody has said on what');
    }
    if (due.length) {
      kind = 'decide';
      reasons.unshift(due.length === 1 ? 'A question to settle in the room' : `${due.length} questions to settle in the room`);
    }
    if (!kind) {
      const days = target ? Math.round((target.getTime() - today.getTime()) / 86_400_000) : null;
      if (!target || days === null || days < 0 || days > HEAR_WINDOW_DAYS) {
        const urgent = i.priority === 'top' || i.priority === 'high';
        const tier: RestTier = (days !== null && days >= 0 && days <= SOON_WINDOW_DAYS) || urgent ? 'soon' : target ? 'later' : 'undated';
        rest.push({ key: i.initiative_key, title: i.title, tier, who: i.owner ?? i.people[0] ?? null, teams: i.teams ?? [], target: i.target_date, priority: i.priority ?? null, laterQuestions: mine, initiative: i });
        continue;
      }
      kind = 'hear';
      reasons.push(`Due ${fmt(target)}: one-sentence status`);
    }
    items.push({
      key: i.initiative_key,
      title: i.title,
      kind,
      reasons,
      who: i.owner ?? i.people[0] ?? null,
      teams: i.teams ?? [],
      target: i.target_date,
      dueQuestions: due,
      laterQuestions: mine.filter((q) => !due.includes(q)),
      minutes: kind === 'decide' ? 4 + 2 * (due.length - 1) : kind === 'unblock' ? 3 : 1,
      initiative: i,
    });
  }
  items.sort((a, b) => KIND_RANK[a.kind] - KIND_RANK[b.kind] || (a.target ?? '9999').localeCompare(b.target ?? '9999') || a.title.localeCompare(b.title));

  const listed = new Set(items.map((x) => x.key));
  const room = new Set(series.room.map((p) => p.toLowerCase()));
  const alsoForTheRoom = live.filter((q) => !listed.has(q.initiative_key) && questionAddressees(q).some((p) => room.has(p.toLowerCase())));
  rest.sort((a, b) =>
    REST_RANK[a.tier] - REST_RANK[b.tier]
    || PRIORITY_RANK[a.priority ?? 'normal'] - PRIORITY_RANK[b.priority ?? 'normal']
    || (a.target ?? '9999').localeCompare(b.target ?? '9999')
    || a.title.localeCompare(b.title));
  return {
    series, date, items, rest, alsoForTheRoom,
    totalMinutes: items.reduce((n, x) => n + x.minutes, 0),
    omitted: { offSeries, closed: all.length - activeAll.length },
    activeTotal: active.length,
  };
}

/** The rule the agenda applies, said once so the split explains itself. */
export const AGENDA_RULE = 'Needs the room is what is late, at risk, stuck, waiting on someone, carrying a question due this week, or due within three weeks; the rest follows by urgency.';

/** The words behind an item: the quotes its due questions carry, then the
 * initiative's own, deduplicated by quote text. */
export function itemQuotes(item: AgendaItem): InitiativeEvidence[] {
  const seen = new Set<string>();
  const out: InitiativeEvidence[] = [];
  const add = (ev: InitiativeEvidence) => { const k = ev.quote.trim().toLowerCase(); if (!seen.has(k)) { seen.add(k); out.push(ev); } };
  for (const q of item.dueQuestions) for (const ev of q.evidence ?? []) add(ev);
  for (const ev of item.initiative.evidence ?? []) add(ev);
  return out;
}
